import { cn } from 'utils/styles';
import { CALCULATOR_RESULT_CARD } from 'components/Widget/shared/cardStyles';
import { CalculatorEnrollmentCta } from 'components/Widget/shared/CalculatorEnrollmentCta';
import { formatCompactCurrency, formatCurrency, toCents } from 'components/Widget/shared/currency';
import { ApySavingsBarGraph } from './ApySavingsBarGraph';
import { BREAKDOWN_ROWS, HERO_COMPACT_THRESHOLD, RESULT_COPY, SEGMENT_FILLS } from './constants';
import type { ApySavingsResult } from './types';

interface ApySavingsCalculatorResultProps {
  result: ApySavingsResult;
  periodLabel: string;
}

const RAISED = 'align-super text-heading-sm';

const ROW_LABEL = 'text-body-saans text-content-secondary';

/** The breakdown reads as primary; the eyebrow and CTA body above and below it stay secondary. */
const BREAKDOWN_ROW = 'text-body-saans text-content-primary';

/** Avoid `<sup>` because global styles and assistive technology treat it as a footnote marker. */
const HeroAmount = ({ value }: { value: number }) => {
  const cents = toCents(value);
  const dollars = Math.trunc(cents / 100).toLocaleString('en-US');

  return (
    <p className="text-heading-lg">
      <span className="sr-only">{formatCurrency(value)}</span>
      {value >= HERO_COMPACT_THRESHOLD ? (
        <span aria-hidden>{formatCompactCurrency(value)}</span>
      ) : (
        <span aria-hidden>
          <span className={RAISED}>$</span>
          {dollars}.<span className={RAISED}>{String(cents % 100).padStart(2, '0')}</span>
        </span>
      )}
    </p>
  );
};

export const ApySavingsCalculatorResult = ({ result, periodLabel }: ApySavingsCalculatorResultProps) => (
  <div className={CALCULATOR_RESULT_CARD}>
    <div>
      <p className={ROW_LABEL}>{RESULT_COPY.eyebrow(periodLabel)}</p>
      <HeroAmount value={result.totalSavings} />
    </div>

    <ApySavingsBarGraph result={result} />

    {/* The chart's bands print nothing, so these rows and their swatches are the chart's legend. */}
    <dl className="flex flex-col gap-3">
      <div className="flex justify-between gap-4 border-b border-border-primary pb-3 text-body-saans font-semibold">
        <dt>{RESULT_COPY.breakdownHeading}</dt>
        <dd>{formatCurrency(result.totalSavings)}</dd>
      </div>
      {BREAKDOWN_ROWS.map(row => (
        <div key={row.key} className="flex justify-between gap-4">
          <dt className={BREAKDOWN_ROW}>
            <span
              aria-hidden
              className={cn('mr-2 inline-block size-3 rounded-full align-middle', SEGMENT_FILLS[row.key])}
            />
            {row.label}
          </dt>
          <dd className={BREAKDOWN_ROW}>{formatCurrency(result[row.key])}</dd>
        </div>
      ))}
    </dl>

    <CalculatorEnrollmentCta />
  </div>
);
